"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui-lab/button"
import { MdOutlineInstallDesktop, MdOutlineInstallMobile } from "react-icons/md"
import useResponsive from "@/lib/useResponsive"

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

export const PwaInstallButton = () => {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)

  const { isMobile } = useResponsive()

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setPromptEvent(event as BeforeInstallPromptEvent)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setPromptEvent(null)
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", handleAppInstalled)

    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
    const isIosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true

    if (isStandalone || isIosStandalone) {
      setIsInstalled(true)
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
      window.removeEventListener("appinstalled", handleAppInstalled)
    }
  }, [])

  const handleInstall = async () => {
    if (!promptEvent) return

    await promptEvent.prompt()
    const choice = await promptEvent.userChoice

    if (choice.outcome === "accepted") {
      setPromptEvent(null)
    }
  }

  if (isInstalled || !promptEvent) return null

  return (
    <Button onClick={handleInstall}>
      {isMobile ? <MdOutlineInstallMobile size={20} /> : <MdOutlineInstallDesktop size={20} />}
      Install App
    </Button>
  )
}
